// Moved from `src/hooks/mutation/useMutateResendTeamInvitation.ts` (now the app
// delegate) with the OT4 mutation slice (ARCFE-1033).
//
// Severed per ARCFE-921: both toasts and the Rematch
// `analyticsScribe.trackResendTeamInvitation` dispatch. `email` stays in the
// mutation variables — the request does not need it, but the delegate's copy
// and analytics payload both do.
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { GET_TEAM_DETAILS } from '../../contracts';
import { resendTeamInvitation } from '../api/teams';
import { isTransportError, type WorkspaceHttpError } from '../config';

export interface ResendTeamInvitationVariables {
  teamId: string;
  invitationId: string;
  /** Display/analytics-only: the request is keyed by `invitationId`. */
  email: string;
}

export interface UseMutateResendTeamInvitationOptions {
  /**
   * App-reaction port: fired where the Rematch `trackResendTeamInvitation`
   * dispatch and the success toast fired, in that order.
   */
  onInvitationResent?: (variables: ResendTeamInvitationVariables) => void;
  /** App-reaction port: fired where the legacy destructive toast fired. */
  onResendError?: (error: WorkspaceHttpError) => void;
}

/** POST /teams/{team_id}/invitations/{invitation_id}/resend */
export function useMutateResendTeamInvitation(
  options?: UseMutateResendTeamInvitationOptions
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: ResendTeamInvitationVariables) => {
      return await resendTeamInvitation(variables.teamId, variables.invitationId);
    },
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({
        queryKey: [GET_TEAM_DETAILS, { teamId: variables.teamId }],
      });
      options?.onInvitationResent?.(variables);
    },
    onError: (error) => {
      if (isTransportError(error)) {
        options?.onResendError?.(error);
      }
    },
  });
}
